import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Folder, File, FileText, AlertCircle, Check, X, Search, ArrowUp,
  Shield, Download, Trash2, Lock, Eye, EyeOff,
  CheckCircle, AlertTriangle, BookOpen
} from 'lucide-react';

const FileSystemSecuritySimulator = ({ scenarios = [], onComplete, difficulty = 'medium', challenge }) => {
  const [fileSystem, setFileSystem] = useState([]);
  const [currentPath, setCurrentPath] = useState('/');
  const [selectedFile, setSelectedFile] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [currentScenario, setCurrentScenario] = useState(0);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackContent, setFeedbackContent] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    console.log('FileSystemSecuritySimulator received scenarios:', scenarios);
    if (scenarios.length > 0) {
      loadScenario(scenarios[0]);
    } else {
      createFallbackFileSystem();
    }
  }, [scenarios]);

  const createFallbackFileSystem = () => {
    const fallbackFiles = [
      // Root directory files
      { name: 'Documents', path: '/Documents', type: 'directory', created: '2024-01-15', modified: '2024-07-20' },
      { name: 'Downloads', path: '/Downloads', type: 'directory', created: '2024-01-15', modified: '2024-07-29' },
      { name: 'Desktop', path: '/Desktop', type: 'directory', created: '2024-01-15', modified: '2024-07-25' },
      { name: 'System32', path: '/System32', type: 'directory', created: '2024-01-15', modified: '2024-07-29', isSystem: true },

      // Suspicious files in Downloads
      {
        name: 'free_antivirus.exe', path: '/Downloads/free_antivirus.exe', type: 'file', fileType: 'executable',
        size: '2.3 MB', created: '2024-07-29', modified: '2024-07-29', isSuspicious: true, isHidden: false,
        permissions: 'rwxrwxrwx', parentPath: '/Downloads', arenaId: 'malware_ransomware',
        threat: 'This executable claims to be antivirus software but was downloaded from an untrusted source.'
      },

      {
        name: 'invoice.pdf.exe', path: '/Downloads/invoice.pdf.exe', type: 'file', fileType: 'executable',
        size: '1.8 MB', created: '2024-07-28', modified: '2024-07-28', isSuspicious: true, isHidden: false,
        permissions: 'rwxrwxrwx', parentPath: '/Downloads', arenaId: 'malware_ransomware',
        threat: 'This file has a double extension (.pdf.exe) which is a common malware technique.'
      },

      {
        name: 'update.bat', path: '/Downloads/update.bat', type: 'file', fileType: 'script',
        size: '856 bytes', created: '2024-07-27', modified: '2024-07-27', isSuspicious: true, isHidden: false,
        permissions: 'rwxrwxrwx', parentPath: '/Downloads', arenaId: 'malware_ransomware',
        threat: 'Batch files can execute malicious commands. This one was downloaded recently.'
      },

      // Normal files
      {
        name: 'vacation_photos.zip', path: '/Downloads/vacation_photos.zip', type: 'file', fileType: 'archive',
        size: '45.2 MB', created: '2024-07-20', modified: '2024-07-20', isSuspicious: false, isHidden: false,
        permissions: 'rw-rw-rw-', parentPath: '/Downloads', arenaId: 'data_protection'
      },

      // Documents folder
      {
        name: 'work_report.docx', path: '/Documents/work_report.docx', type: 'file', fileType: 'document',
        size: '2.1 MB', created: '2024-07-15', modified: '2024-07-25', isSuspicious: false, isHidden: false,
        permissions: 'rw-rw-rw-', parentPath: '/Documents', arenaId: 'data_protection'
      },

      {
        name: 'passwords.txt', path: '/Documents/passwords.txt', type: 'file', fileType: 'text',
        size: '1.2 KB', created: '2024-06-10', modified: '2024-07-10', isSuspicious: true, isHidden: false,
        permissions: 'rw-rw-rw-', parentPath: '/Documents', arenaId: 'data_protection',
        threat: 'Storing passwords in plain text files is a serious security risk.'
      },

      // Hidden system files
      {
        name: '.hidden_keylogger.exe', path: '/System32/.hidden_keylogger.exe', type: 'file', fileType: 'executable',
        size: '3.4 MB', created: '2024-07-25', modified: '2024-07-29', isSuspicious: true, isHidden: true,
        permissions: 'rwxrwxrwx', parentPath: '/System32', arenaId: 'malware_ransomware',
        threat: 'Hidden executable in system directory - likely malware or keylogger.'
      },

      // Desktop files
      {
        name: 'Chrome.lnk', path: '/Desktop/Chrome.lnk', type: 'file', fileType: 'shortcut',
        size: '2 KB', created: '2024-01-15', modified: '2024-01-15', isSuspicious: false, isHidden: false,
        permissions: 'rw-rw-rw-', parentPath: '/Desktop', arenaId: 'secure_browsing'
      }
    ];

    setFileSystem(fallbackFiles);
  };

  const loadScenario = (scenario) => {
    if (scenario.fileSystem) {
      setFileSystem(scenario.fileSystem);
    } else {
      createFallbackFileSystem();
    }
    setCurrentPath('/');
    setSelectedFile(null);
  };

  const getCurrentDirectoryItems = () => {
    if (searchQuery.trim() !== '') {
      return fileSystem.filter(item =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        (difficulty === 'easy' || !item.isHidden)
      );
    }

    if (currentPath === '/') {
      return fileSystem.filter(item =>
        !item.parentPath && (difficulty === 'easy' || !item.isHidden)
      );
    }

    return fileSystem.filter(item =>
      item.parentPath === currentPath && (difficulty === 'easy' || !item.isHidden)
    );
  };

  const handleFileSelect = (file) => {
    if (showFeedbackModal) return;
    setSelectedFile(file);
  };

  const navigateToDirectory = (path, name) => {
    setCurrentPath(path);
    setSelectedFile(null);
  };

  const handleAction = (action) => {
    if (!selectedFile) return;

    const isCorrectAction = (action === 'quarantine' || action === 'delete') && selectedFile.isSuspicious;
    const isIncorrectAction = (action === 'allow' && selectedFile.isSuspicious) || ((action === 'quarantine' || action === 'delete') && !selectedFile.isSuspicious);

    let feedback = {
      isCorrect: isCorrectAction,
      title: isCorrectAction ? 'Correct Action!' : 'Incorrect Action!',
      explanation: selectedFile.threat || (isCorrectAction ? 'You correctly handled a suspicious file.' : 'This was not the correct action for this file.'),
      indicators: selectedFile.isSuspicious ? [
        `File name: ${selectedFile.name}`,
        `File type: ${selectedFile.fileType}`,
        `Permissions: ${selectedFile.permissions}`,
        `Hidden: ${selectedFile.isHidden ? 'Yes' : 'No'}`
      ] : []
    };

    const decision = {
      fileId: selectedFile.path, // Use path as a unique ID
      action: action,
      correct: isCorrectAction,
      timestamp: Date.now()
    };

    setDecisions(prev => ({
      ...prev,
      [selectedFile.path]: decision
    }));

    setFeedbackContent(feedback);
    setShowFeedbackModal(true);
  };

  const handleCloseFeedbackModal = () => {
    const action = decisions[selectedFile.path]?.action;

    if (action === 'quarantine' || action === 'delete') {
      setFileSystem(prev => prev.filter(f => f.path !== selectedFile.path));
      setSelectedFile(null);
    }

    setShowFeedbackModal(false);
    setFeedbackContent(null);
    checkCompletion();
  };

  const handleLearnMore = () => {
    if (challenge?.metadata) {
      window.open(challenge.metadata, '_blank', 'noopener,noreferrer');
    } else {
      const arenaId = selectedFile?.arenaId || 'os_security'; // Fallback
      if (arenaId) {
        navigate(`/learning/module/${arenaId}?topic=${arenaId}`);
      }
    }
  };

  const checkCompletion = () => {
    const suspiciousFiles = fileSystem.filter(f => f.isSuspicious);
    const identifiedThreats = Object.keys(decisions).length;

    if (identifiedThreats >= suspiciousFiles.length && suspiciousFiles.length > 0) {
      setTimeout(() => {
        const correctDecisions = Object.values(decisions).filter(d => d.correct).length;
        const summary = {
          correct: correctDecisions,
          total: suspiciousFiles.length,
          score: Math.round((correctDecisions / suspiciousFiles.length) * 100)
        };

        if (onComplete) {
          onComplete(summary);
        }
      }, 1000);
    }
  };

  const renderBreadcrumbs = () => (
    <div className="bg-gray-100 p-2 border-b flex items-center space-x-2 text-sm">
      <button
        onClick={() => navigateToDirectory('/', 'Home')}
        className="text-blue-600 hover:text-blue-800"
      >
        Home
      </button>
      {currentPath !== '/' && (
        <>
          <span>/</span>
          <span className="text-gray-600">{currentPath.split('/').pop()}</span>
        </>
      )}
    </div>
  );

  const renderToolbar = () => (
    <div className="bg-gray-50 p-3 border-b flex items-center justify-between">
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigateToDirectory('/', 'Home')}
          className="flex items-center px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          <ArrowUp size={16} className="mr-1" />
          Up
        </button>

        <div className="flex items-center">
          <Search size={16} className="text-gray-500 mr-2" />
          <input
            type="text"
            placeholder="Search files..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="px-3 py-1 border rounded focus:outline-none focus:border-blue-500"
          />
        </div>
      </div>

      <div className="text-sm text-gray-600">
        Find and identify {fileSystem.filter(f => f.isSuspicious).length} security threats
      </div>
    </div>
  );

  const renderFileList = () => {
    const items = getCurrentDirectoryItems();

    if (items.length === 0) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-gray-500">
            <Folder size={48} className="mx-auto mb-2 opacity-50" />
            <p>No files found</p>
          </div>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 p-3">
        {items.map((item) => (
          <div
            key={item.path}
            className={`p-3 rounded-lg cursor-pointer hover:bg-gray-100 flex flex-col items-center text-center border ${selectedFile?.path === item.path ? 'bg-blue-100 border-blue-300' : 'border-gray-200'
              } ${item.isSuspicious ? 'border-red-300 bg-red-50' : ''}`}
            onClick={() => {
              if (item.type === 'directory') {
                navigateToDirectory(item.path, item.name);
              } else {
                handleFileSelect(item);
              }
            }}
          >
            <div className="relative">
              {item.type === 'directory' ? (
                <Folder size={40} className="text-yellow-500 mb-2" />
              ) : (
                <>
                  {item.fileType === 'executable' && (
                    <File size={40} className={item.isSuspicious ? "text-red-500 mb-2" : "text-blue-500 mb-2"} />
                  )}
                  {item.fileType === 'text' && <FileText size={40} className="text-green-500 mb-2" />}
                  {item.fileType === 'document' && <FileText size={40} className="text-purple-500 mb-2" />}
                  {item.fileType === 'archive' && <File size={40} className="text-orange-500 mb-2" />}
                  {item.fileType === 'script' && <File size={40} className="text-red-600 mb-2" />}
                  {!item.fileType && <File size={40} className="text-gray-500 mb-2" />}
                </>
              )}

              {item.isSuspicious && (
                <AlertCircle size={16} className="absolute -top-1 -right-1 text-red-600 bg-white rounded-full" />
              )}

              {item.isHidden && difficulty !== 'easy' && (
                <EyeOff size={12} className="absolute top-0 left-0 text-gray-400" />
              )}
            </div>

            <span className="text-sm font-medium truncate w-full text-gray-800">{item.name}</span>
            <span className="text-xs text-gray-600">{item.size || item.type}</span>

            {item.isSuspicious && difficulty === 'easy' && (
              <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded-full mt-1">
                Suspicious
              </span>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderFileDetails = () => {
    if (!selectedFile) return null;

    return (
      <div className="w-80 border-l border-gray-300 p-4 bg-gray-50">
        <h3 className="font-bold mb-3 flex items-center text-gray-800">
          {selectedFile.isSuspicious && <AlertCircle size={16} className="text-red-500 mr-2" />}
          {selectedFile.name}
        </h3>

        <div className="space-y-3 text-sm text-gray-800">
          <div>
            <span className="font-medium text-gray-600">Type:</span>
            <span className="ml-2">{selectedFile.type === 'directory' ? 'Folder' : selectedFile.fileType || 'File'}</span>
          </div>

          {selectedFile.size && (
            <div>
              <span className="font-medium text-gray-600">Size:</span>
              <span className="ml-2">{selectedFile.size}</span>
            </div>
          )}

          <div>
            <span className="font-medium text-gray-600">Created:</span>
            <span className="ml-2">{selectedFile.created}</span>
          </div>

          <div>
            <span className="font-medium text-gray-600">Modified:</span>
            <span className="ml-2">{selectedFile.modified}</span>
          </div>

          <div>
            <span className="font-medium text-gray-600">Permissions:</span>
            <span className={`ml-2 ${selectedFile.permissions === 'rwxrwxrwx' ? 'text-red-600 font-bold' : ''}`}>
              {selectedFile.permissions}
            </span>
          </div>

          {selectedFile.isSuspicious && (
            <div className="bg-red-100 border border-red-300 p-3 rounded">
              <div className="flex items-center mb-2">
                <AlertCircle size={16} className="text-red-600 mr-2" />
                <span className="font-bold text-red-800">Security Threat Detected</span>
              </div>
              <p className="text-red-700 text-sm">{selectedFile.threat}</p>
            </div>
          )}

          {selectedFile.type === 'file' && (
            <div className="space-y-2 pt-4 border-t">
              <button
                onClick={() => handleAction('quarantine')}
                className="w-full px-3 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 flex items-center justify-center"
              >
                <Shield size={16} className="mr-2" />
                Quarantine
              </button>

              <button
                onClick={() => handleAction('delete')}
                className="w-full px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center justify-center"
              >
                <Trash2 size={16} className="mr-2" />
                Delete
              </button>

              <button
                onClick={() => handleAction('allow')}
                className="w-full px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center justify-center"
              >
                <Check size={16} className="mr-2" />
                Allow
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderFeedbackModal = () => {
    if (!showFeedbackModal || !feedbackContent) return null;

    const { isCorrect, title, explanation, indicators } = feedbackContent;

    return (
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full p-8 m-4 animate-fade-in-up">
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center gap-3">
              {isCorrect ? (
                <CheckCircle size={28} className="text-green-600" />
              ) : (
                <AlertTriangle size={28} className="text-red-600" />
              )}
              <h2 className={`text-2xl font-bold ${isCorrect ? 'text-green-800' : 'text-red-800'}`}>
                {title}
              </h2>
            </div>
            <button onClick={handleCloseFeedbackModal} className="text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
          </div>

          <div className="text-gray-700 space-y-4">
            <p className="text-base">{explanation}</p>

            {indicators && indicators.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h3 className="font-bold text-yellow-800 flex items-center">
                  <AlertTriangle size={16} className="mr-2" />
                  Key Indicators Found:
                </h3>
                <ul className="list-disc list-inside space-y-1.5 mt-2 text-gray-700 text-sm pl-2">
                  {indicators.map((indicator, index) => (
                    <li key={index}>{indicator}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Modal Footer with Buttons */}
          <div className="mt-8 flex justify-end items-center gap-4">
            <button
              onClick={handleLearnMore}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-purple-700 bg-purple-100 rounded-lg hover:bg-purple-200 transition-colors"
            >
              <BookOpen size={16} />
              Learn More
            </button>
            <button
              onClick={handleCloseFeedbackModal}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col bg-white relative">
      <div className="bg-gray-800 text-white p-3 flex items-center justify-between">
        <div className="flex items-center">
          <Folder size={20} className="mr-2" />
          <span className="font-medium">File Security Scanner</span>
        </div>
        <div className="text-sm">
          Threats Found: {Object.keys(decisions).length} / {fileSystem.filter(f => f.isSuspicious).length}
        </div>
      </div>

      {renderBreadcrumbs()}
      {renderToolbar()}

      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 overflow-auto">
          {renderFileList()}
        </div>
        {renderFileDetails()}
      </div>

      {renderFeedbackModal()}
    </div>
  );
};

export default FileSystemSecuritySimulator;